// noinspection DuplicatedCode

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Select from '@/modules/common/components/Select';
import TextField from '@/modules/common/components/TextField';
import DialogForm from '@/modules/common/components/DialogForm';
import DateTimePicker from '@/modules/common/components/DateTimePicker';
import { withFetch } from '~/hocs/fetch';

const initialValues = {
    affiliate: '',
    type: '',
    status: '',
    datetimeStatus: '',
    comment: null,
};

const initialErrors = {
    affiliate: '',
    type: '',
    status: '',
    datetimeStatus: '',
    comment: '',
};

let abortController;

const DialogEdit = (props) => {
    const [values, setValues] = useState(initialValues);
    const [errors, setErrors] = useState(initialErrors);
    const [fetching, setFetching] = useState(false);

    const getAffiliateOptions = () => {
        return props.affiliates.map((affiliate) => ({title: affiliate.affiliate_name, value: affiliate.id.toString()}));
    };

    const getTypeOptions = () => {
        const offer = (props.selectedLead) ? props.selectedLead.offer.id : null;
        let options = [];
        if (offer >= 0) {
            props.types.forEach((type) => {
                if (type.offer.id === offer) {
                    options.push({title: type.name, value: type.id.toString()});
                }
            });
        }
        return options;
    };

    const getStatusOptions = () => {
        const offer = (props.selectedLead) ? props.selectedLead.offer.id : null;
        let options = [];
        if (offer >= 0) {
            props.statuses.forEach((status) => {
                if (status.offer.id === offer) {
                    options.push({title: status.status, value: status.id.toString()});
                }
            });
        }
        return options;
    };

    const clear = () => {
        setValues(initialValues);
        setErrors(initialErrors);
    };

    useEffect(() => {
        return () => {
            if (abortController) {
                abortController.abort();
            }
        };
    }, []);

    useEffect(() => {
        if (props.selectedLead) {
            let affiliate_id = '';
            props.affiliates.every((affiliate) => {
                if (affiliate.affiliate_id === props.selectedLead.affiliate_id) {
                    affiliate_id = affiliate.id.toString();
                    return false;
                }
                return true;
            });
            const comment = props.selectedLead.comment;
            // noinspection JSUnresolvedVariable
            setValues({
                affiliate: affiliate_id,
                type: (props.selectedLead.type) ? props.selectedLead.type.id.toString() : '',
                status: (props.selectedLead.status) ? props.selectedLead.status.id.toString() : '',
                datetimeStatus: new Date(props.selectedLead.datetime_status),
                comment: (comment && comment.length > 0) ? comment : null,
            });
        }
    }, [props.selectedLead]);

    const handleChange = (prop) => (event) => {
        let value = event.target.value;
        if (prop === 'comment') {
            if (!value.length) {
                value = null;
            }
        }
        setValues({...values, [prop]: value});
    };

    const handleFocus = (prop) => () => {
        setErrors({...errors, [prop]: ''});
    };

    const handleSubmit = (event) => {
        abortController = new AbortController();

        event.preventDefault();
        setFetching(true);
        props.fetch.put(
            `/api/leads/${props.selectedLead.id}/`, {
                affiliate: values.affiliate,
                type: values.type,
                status: values.status,
                datetime_status: values.datetimeStatus,
                comment: values.comment
            },
            abortController
        ).then(({ok, data}) => {
            if (ok) {
                clear();
                props.onOk(data);
            }
            else {
                let _errors = {...initialErrors};
                _errors.affiliate = (data.affiliate) ? data.affiliate[0] : '';
                _errors.type = (data.type) ? data.type[0] : '';
                _errors.status = (data.status) ? data.status[0] : '';
                _errors.datetimeStatus = (data.datetime_status) ? data.datetime_status[0] : '';
                _errors.comment = (data.comment) ? data.comment[0] : '';
                setErrors(_errors);
            }
            setFetching(false);
        }).catch((error) => {
            if (error.name !== 'AbortError') {
                setFetching(false);
            }
        }).finally(() => {
            abortController = null;
        });
    };

    const handleClose = () => {
        props.setOpen(false);
        clear();
        props.setSelectedLead(null);
    };

    return (
        <DialogForm title="Изменение лида" label="Изменить"
                    open={props.open} onClose={handleClose}
                    onSubmit={handleSubmit}
                    disabled={fetching}
        >
            <Select label="Партнер" options={getAffiliateOptions()}
                    value={values.affiliate}
                    onChange={handleChange('affiliate')}
                    error={errors.affiliate.length > 0}
                    helperText={errors.affiliate}
                    onFocus={handleFocus('affiliate')}
                    autoFocus={true}
            />
            <Select label="Тип заявки" options={getTypeOptions()}
                    value={values.type}
                    onChange={handleChange('type')}
                    error={errors.type.length > 0}
                    helperText={errors.type}
                    onFocus={handleFocus('type')}
            />
            <Select label="Статус" options={getStatusOptions()}
                    value={values.status}
                    onChange={handleChange('status')}
                    error={errors.status.length > 0}
                    helperText={errors.status}
                    onFocus={handleFocus('status')}
                    showEmpty={false}
            />
            <DateTimePicker label="Дата/Время статуса"
                            value={values.datetimeStatus}
                            setValue={(value) => setValues({...values, datetimeStatus: value})}
                            error={errors.datetimeStatus.length > 0}
                            helperText={errors.datetimeStatus}
                            onFocus={handleFocus('datetimeStatus')}
            />
            <TextField label="Комментарий"
                       value={values.comment}
                       onChange={handleChange('comment')}
                       error={errors.comment.length > 0}
                       helperText={errors.comment}
                       onFocus={handleFocus('comment')}
            />
        </DialogForm>
    );
};

DialogEdit.propTypes = {
    selectedLead: PropTypes.object,
    setSelectedLead: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    setOpen: PropTypes.func.isRequired,
    onOk: PropTypes.func.isRequired,
};

let Component = DialogEdit;
Component = withFetch(Component);

export default connect(
    (state) => ({
        types: state.types,
        statuses: state.statuses,
        affiliates: state.affiliates,
    })
)(Component);
